/**
 * iter-238 — Module Access Guard.
 *
 * When a user has `restrictions.allowed_module_keys`, redirect them
 * silently to the first child of their first allowed module whenever
 * they land on a path that isn't part of an allowed module.
 *
 * Currently used to lock Alvarez & Marsal demo accounts into the
 * Category & Spend module only, without needing per-route code changes.
 */
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import {
  PROCUREMENT_MODULES, CONTRACTS_MODULES, CLAIMS_LD_MODULES,
} from '@/config/navigation';

const ALL_MODULES = [
  ...PROCUREMENT_MODULES,
  ...CONTRACTS_MODULES,
  ...CLAIMS_LD_MODULES,
];

// Paths that stay reachable regardless of module restrictions —
// profile, notifications, admin (backend enforces its own RBAC).
const ALWAYS_ALLOWED_PREFIXES = [
  '/app/profile',
  '/app/notifications',
  '/app/admin',
  '/app/superadmin',
  '/superadmin',
];

function isPathInAllowedModules(pathname, allowedKeys) {
  const stripQ = (p) => (p || '').split('?')[0];
  for (const mod of ALL_MODULES) {
    if (!allowedKeys.includes(mod.key)) continue;
    for (const child of mod.children || []) {
      const cp = stripQ(child.path);
      if (pathname === cp || pathname.startsWith(cp + '/')) return true;
    }
  }
  return false;
}

function firstAllowedPath(allowedKeys) {
  for (const mod of ALL_MODULES) {
    if (!allowedKeys.includes(mod.key)) continue;
    const firstChild = mod.children?.[0];
    if (firstChild?.path) return firstChild.path;
  }
  return null;
}

export default function ModuleAccessGuard() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const allowedKeys = useMemo(
    () => user?.restrictions?.allowed_module_keys || null,
    [user],
  );

  useEffect(() => {
    if (!allowedKeys || allowedKeys.length === 0) return;
    if (!location.pathname.startsWith('/app/')) return;
    if (ALWAYS_ALLOWED_PREFIXES.some(p => location.pathname.startsWith(p))) return;
    if (location.pathname === '/app' || location.pathname === '/app/') {
      const target = firstAllowedPath(allowedKeys);
      if (target) navigate(target, { replace: true });
      return;
    }
    if (!isPathInAllowedModules(location.pathname, allowedKeys)) {
      const target = firstAllowedPath(allowedKeys);
      if (target) navigate(target, { replace: true });
    }
  }, [location.pathname, allowedKeys, navigate]);

  return null;
}
